import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '@/services/apiService';
import { TourOverlay } from './TourOverlay';
import { useTourGuide } from './TourContext';
import { TourWelcomeModal } from './TourWelcomeModal';
import { userPreferencesService } from '@/services/userPreferencesService';
import { getPostLoginTour, getTourForPath, type TourDefinition } from './pageTours';

export default function PostLoginTourGate() {
  const location = useLocation();
  const { status, openWelcome, start, close } = useTourGuide();
  const [shouldShowWelcome, setShouldShowWelcome] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null);

  // Prevent re-opening multiple times in same session.
  const openedRef = useRef(false);

  const pathTour = useMemo(() => getTourForPath(location.pathname), [location.pathname]);
  const postLoginTour = useMemo(() => getPostLoginTour(), []);
  const onAccountShell = location.pathname.startsWith('/account');
  const isHome = location.pathname === '/' || location.pathname === '';
  /** Chrome (header) tour can run on Home or any /account/* shell. */
  const canRunChromeTour = isHome || onAccountShell;
  const hasToken = Boolean(apiService.getAccessToken());

  const refreshEligibility = useCallback(async () => {
    if (!hasToken) {
      setLoading(false);
      setShouldShowWelcome(false);
      setActiveTour(null);
      return;
    }

    if (userPreferencesService.isTourGuidesDismissedLocally()) {
      setLoading(false);
      setShouldShowWelcome(false);
      setActiveTour(null);
      return;
    }

    try {
      setLoading(true);

      if (canRunChromeTour) {
        const hubPref = await userPreferencesService.getTourPreference(postLoginTour.id);
        const hubDone =
          hubPref?.version === postLoginTour.version && hubPref?.completed === true;
        if (!hubDone) {
          setActiveTour(postLoginTour);
          setShouldShowWelcome(true);
          return;
        }
      }

      if (!pathTour) {
        setActiveTour(null);
        setShouldShowWelcome(false);
        return;
      }

      const pref = await userPreferencesService.getTourPreference(pathTour.id);
      const completed = pref?.version === pathTour.version && pref?.completed === true;
      setActiveTour(pathTour);
      setShouldShowWelcome(!completed);
    } catch {
      // Fail-open (show), but keep a local guard so we don't spam.
      setActiveTour(canRunChromeTour ? postLoginTour : pathTour);
      setShouldShowWelcome(true);
    } finally {
      setLoading(false);
    }
  }, [hasToken, canRunChromeTour, pathTour, postLoginTour]);

  useEffect(() => {
    void refreshEligibility();
  }, [refreshEligibility]);

  useEffect(() => {
    openedRef.current = false;
  }, [activeTour?.id]);

  useEffect(() => {
    if (loading) return;
    if (!shouldShowWelcome) return;
    if (openedRef.current) return;
    if (!hasToken || !activeTour) return;
    if (status !== 'idle') return;

    openedRef.current = true;
    openWelcome(activeTour.steps);
  }, [loading, shouldShowWelcome, hasToken, activeTour, status, openWelcome]);

  const handleClose = useCallback(() => {
    userPreferencesService.dismissAllTourGuides();
    if (activeTour) {
      void userPreferencesService.setTourCompleted({
        tourId: activeTour.id,
        version: activeTour.version,
      });
    }
    openedRef.current = true;
    setShouldShowWelcome(false);
    close();
  }, [close, activeTour]);

  const handleStart = useCallback(() => {
    start();
  }, [start]);

  const handleFinish = useCallback(async () => {
    try {
      if (activeTour) {
        await userPreferencesService.setTourCompleted({
          tourId: activeTour.id,
          version: activeTour.version,
        });
      }
      setShouldShowWelcome(false);
    } finally {
      close();
      // After chrome on Home, allow the lobby page tour to follow in the same visit.
      openedRef.current = false;
      void refreshEligibility();
    }
  }, [close, activeTour, refreshEligibility]);

  return (
    <>
      <TourWelcomeModal
        isOpen={status === 'welcome'}
        title={activeTour?.welcomeTitle ?? 'Welcome'}
        description={activeTour?.welcomeDescription ?? ''}
        onClose={handleClose}
        onStart={handleStart}
      />
      <TourOverlay onRequestClose={handleClose} onRequestFinish={handleFinish} />
    </>
  );
}
